import React, { useState, useEffect } from "react";
import moment from "moment";
import { getRequest, postRequest } from "../../utils/api";
import Loader from "../../Component/Loader/Loader";
import PaginationComponent from "../../Component/Pagination/pagination";
import { logout } from "../../utils/auth";
import { toast } from "react-toastify";
import { useParams } from "react-router-dom"; // Add import
// --- 1. Define TypeScript Interfaces ---

interface PricingRecord {
  _id: string;
  country_name: string;
  country_code: string;
  meta_marketing_price: number;
  our_marketing_price: number;
  meta_utility_price: number;
  our_utility_price: number;
  meta_authentication_price: number;
  our_authentication_price: number;
  meta_service_price: number;
  our_service_price: number;
  // Where the "Ours" price comes from (on a user's page): custom — set for this user,
  // plan — the user's plan markup on Meta's base, global — the global price
  price_source?: 'custom' | 'plan' | 'global';
  status: number; // 1 active, 0 inactive
  last_synced: string;
}

interface UserPlan {
  _id: string;
  title: string;
  markups: { marketing: number; utility: number; authentication: number; service: number };
}

interface PaginationData {
  total: number;
  limit: number;
  page: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
}

interface ApiResponse {
  status: string;
  data: PricingRecord[];
  pagination: PaginationData;
  userPlan?: UserPlan | null;
  message?: string;
}

// Label for where a user's price comes from
const SOURCE_BADGE: Record<string, [string, string]> = {
  custom: ["badge-soft-primary", "Custom"],
  plan: ["badge-soft-success", "Plan"],
  global: ["badge-soft-secondary", "Global"],
};

// --- 2. Apply Types to Component ---

const MetaPricingSync: React.FC = () => {
  const { userId } = useParams<{ userId?: string }>();

  const [pricingList, setPricingList] = useState<PricingRecord[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [page, setPage] = useState<number>(1);
  const [pagination, setPagination] = useState<PaginationData>({
    total: 0,
    limit: 100,
    page: 1,
    totalPages: 1,
    hasNextPage: false,
    hasPrevPage: false
  });

  // --- Inline Editing State ---
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState({
    our_marketing_price: 0,
    our_utility_price: 0,
    our_authentication_price: 0,
    our_service_price: 0,
  });
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [markupPercentage, setMarkupPercentage] = useState<number | string>("");
  const [isApplyingMarkup, setIsApplyingMarkup] = useState<boolean>(false);
  const [isImportingOct2026, setIsImportingOct2026] = useState<boolean>(false);
  const [isBackfillingService, setIsBackfillingService] = useState<boolean>(false);
  // On a user's page: the plan whose markup gives the default prices (null → no plan, global prices)
  const [userPlan, setUserPlan] = useState<UserPlan | null>(null);
  // Fetch pricing from your database
  const fetchPricingList = async () => {
    setLoading(true);
    try {
      // Append matching custom user parameter dynamically if editing context is found
      const urlPath = userId
        ? `/plan/country-rates?page=${page}&userId=${userId}`
        : `/plan/country-rates?page=${page}`;

      const response = await getRequest(urlPath) as ApiResponse;

      if (response.status === "success") {
        setPricingList(response.data);
        setPagination(response.pagination);
        setUserPlan(response.userPlan || null);
      } else if (response.status === 'session_expired') {
        logout();
      } else {
        toast.error(response.message || "Failed to load pricing data.");
      }
    } catch (error) {
      console.error("Error fetching pricing:", error);
      toast.error("Something went wrong while fetching data.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPricingList();
    // Cancel editing if page changes
    setEditingId(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  // --- Inline Edit Handlers ---
  const handleEditClick = (record: PricingRecord) => {
    setEditingId(record._id);
    setEditForm({
      our_marketing_price: record.our_marketing_price,
      our_utility_price: record.our_utility_price,
      our_authentication_price: record.our_authentication_price,
      our_service_price: record.our_service_price,
    });
  };

  const handleCancelEdit = () => {
    setEditingId(null);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setEditForm(prev => ({
      ...prev,
      [name]: parseFloat(value) || 0
    }));
  };

  // Modify save method handler inside your form handler to pass country contexts along
  const handleSaveEdit = async (record: any) => {
    console.log('record', record);

    setIsSaving(true);
    try {
      const payload = {
        pricing_id: record._id,
        userId: userId || undefined,
        country_code: record.country_code,
        ...editForm
      };

      const response = await postRequest('/plan/update-margins', payload);

      if (response.status === 'success') {
        toast.success(userId ? "User custom price updated!" : "Global price updated!");
        setEditingId(null);
        fetchPricingList();
      } else {
        toast.error(response.message || "Failed to save updates.");
      }
    } catch (error) {
      toast.error("An error occurred while saving changes.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleApplyMarkup = async () => {
    const percentage = parseFloat(markupPercentage as string);

    if (isNaN(percentage) || percentage <= 0) {
      toast.error("Please enter a valid percentage greater than 0");
      return;
    }

    const confirmMessage = userId
      ? `Are you sure you want to apply a ${percentage}% markup to this specific user?`
      : `Are you sure you want to apply a ${percentage}% markup to all Base Meta prices globally?`;

    if (!window.confirm(confirmMessage)) {
      return;
    }

    setIsApplyingMarkup(true);
    try {
      // ✅ Added userId to the payload
      const payload = { percentage, userId: userId || undefined };
      const response = await postRequest('/plan/apply-global-markup', payload) as ApiResponse;

      if (response.status === 'success') {
        toast.success(response.message);
        setMarkupPercentage("");
        fetchPricingList();
      } else {
        toast.error(response.message || "Failed to apply markup.");
      }
    } catch (error) {
      console.error("Error applying markup:", error);
      toast.error("An error occurred while applying the markup.");
    } finally {
      setIsApplyingMarkup(false);
    }
  };

  const handleImportOct2026Rates = async () => {
    if (!window.confirm('Import Meta\'s Oct 1, 2026 rate card? This updates the Meta base price (left column) for every standalone market and regional grouping — your own custom margins ("Ours" column) are left untouched. Safe to re-run if a rate changes before the effective date.')) {
      return;
    }

    setIsImportingOct2026(true);
    try {
      const response = await postRequest('/plan/import-oct-2026-rates', {}) as ApiResponse;

      if (response.status === 'success') {
        toast.success(response.message, { autoClose: 12000 });
        fetchPricingList();
      } else {
        toast.error(response.message || "Failed to import the Oct 2026 rate card.");
      }
    } catch (error) {
      console.error("Error importing Oct 2026 rates:", error);
      toast.error("An error occurred while importing the rate card.");
    } finally {
      setIsImportingOct2026(false);
    }
  };

  const handleBackfillServiceFromUtility = async () => {
    if (!window.confirm('Set every user\'s custom Service price equal to their custom Utility price (cs_user_pricings only — the global table is untouched)? Meta prices Service the same as Utility in every market, so this is a safe default for any user who already has a custom utility markup but never had one set for service.')) {
      return;
    }

    setIsBackfillingService(true);
    try {
      const response = await postRequest('/plan/backfill-service-price-from-utility', {}) as ApiResponse;

      if (response.status === 'success') {
        toast.success(response.message, { autoClose: 12000 });
        fetchPricingList();
      } else {
        toast.error(response.message || "Failed to backfill service pricing.");
      }
    } catch (error) {
      console.error("Error backfilling service price:", error);
      toast.error("An error occurred while backfilling service pricing.");
    } finally {
      setIsBackfillingService(false);
    }
  };

  return (
    <>
      {loading && <Loader />}
      <div className="hk-pg-wrapper">
        <div className="hk-pg-body py-0">
          <div className="page-wrap">
            <div className="page-content ps-0">
              <div className="page-detail-wrap">
                <header className="page-header d-flex justify-content-between align-items-center flex-wrap gap-3">
                  <div>
                    <div className="page-title">
                      <div>
                        <h2 className="mb-1">Meta Pricing & Credit Line</h2>
                        <p className="mb-0 fs-13">Sync base rates from Meta and configure your custom charging margins per country.</p>
                        {userId && (
                          <p className="mb-0 mt-1 fs-13">
                            {userPlan ? (
                              <>
                                Default prices for this user come from their plan <b>{userPlan.title}</b>:
                                Meta base + marketing {userPlan.markups.marketing}% · utility {userPlan.markups.utility}% ·
                                authentication {userPlan.markups.authentication}% · service {userPlan.markups.service}%.
                                Editing a country saves a custom price for this user only.
                              </>
                            ) : (
                              <>This user has no plan, so their default prices are the global prices. Editing a country saves a custom price for this user only.</>
                            )}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* GLOBAL MARKUP SECTION */}
                  <div className="d-flex align-items-center gap-2">
                    {!userId && (
                      <button
                        className="btn btn-outline-dark"
                        onClick={handleImportOct2026Rates}
                        disabled={isImportingOct2026}
                        title="Updates Meta's base price for every market to the Oct 1, 2026 rate card — your custom margins are untouched"
                      >
                        {isImportingOct2026 ? (
                          <i className="ri-loader-4-line fa-spin"></i>
                        ) : (
                          <>
                            <i className="ri-download-cloud-2-line me-1"></i> Import Oct 2026 Rates
                          </>
                        )}
                      </button>
                    )}
                    {!userId && (
                      <button
                        className="btn btn-outline-dark"
                        onClick={handleBackfillServiceFromUtility}
                        disabled={isBackfillingService}
                        title="Sets every user's custom Service price to match their custom Utility price (cs_user_pricings only)"
                      >
                        {isBackfillingService ? (
                          <i className="ri-loader-4-line fa-spin"></i>
                        ) : (
                          <>
                            <i className="ri-file-copy-2-line me-1"></i> Service = Utility
                          </>
                        )}
                      </button>
                    )}
                    <div className="input-group" style={{ width: '220px' }}>
                      <span className="input-group-text bg-light text-muted fs-13">Markup</span>
                      <input
                        type="number"
                        className="form-control"
                        placeholder="e.g. 10"
                        value={markupPercentage}
                        onChange={(e) => setMarkupPercentage(e.target.value)}
                        disabled={isApplyingMarkup}
                        min="1"
                      />
                      <span className="input-group-text bg-light text-muted fw-bold">%</span>
                    </div>
                    <button
                      className="btn text-white"
                      style={{ backgroundColor: '#3e3e8a', borderColor: '#3e3e8a' }}
                      onClick={handleApplyMarkup}
                      disabled={isApplyingMarkup || !markupPercentage}
                    >
                      {isApplyingMarkup ? (
                        <i className="ri-loader-4-line fa-spin"></i>
                      ) : (
                        <>
                          <i className="ri-percent-line me-1"></i> Apply
                        </>
                      )}
                    </button>
                  </div>
                </header>
                <div className="page-body">
                  <div data-simplebar className="nicescroll-bar">
                    <div className="container-fluid">
                      <div className="row">
                        <div className="col-lg-12">
                          <div className="card">
                            <div className="table-responsive">
                              <table className="table table-hover mb-0 align-middle">
                                <thead>
                                  <tr>
                                    <th>Country</th>
                                    <th>Marketing <br /><small className="text-muted">(Meta | Ours)</small></th>
                                    <th>Utility <br /><small className="text-muted">(Meta | Ours)</small></th>
                                    <th>Authentication <br /><small className="text-muted">(Meta | Ours)</small></th>
                                    <th>Service <br /><small className="text-muted">(Meta | Ours)</small></th>
                                    <th className="text-center">Last Synced</th>
                                    <th className="text-center">Action</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {pricingList.length > 0 ? (
                                    pricingList.map((record) => {
                                      const isEditing = editingId === record._id;

                                      return (
                                        <tr key={record._id}>
                                          <td>
                                            <div className="d-flex align-items-center">
                                              <span className="fw-bold text-dark">{record.country_name}</span>
                                              <span className="badge badge-soft-secondary ms-2">{record.country_code}</span>
                                              {userId && record.price_source && (
                                                <span
                                                  className={`badge ${SOURCE_BADGE[record.price_source][0]} ms-1`}
                                                  title={record.price_source === 'custom'
                                                    ? "Custom price set for this user"
                                                    : record.price_source === 'plan'
                                                      ? `From the user's plan${userPlan ? ` (${userPlan.title})` : ''}`
                                                      : "Global price (user has no plan)"}
                                                >
                                                  {SOURCE_BADGE[record.price_source][1]}
                                                </span>
                                              )}
                                            </div>
                                          </td>

                                          {/* MARKETING */}
                                          <td>
                                            <span className="text-muted">₹{record.meta_marketing_price}</span>
                                            <span className="mx-1">|</span>
                                            {isEditing ? (
                                              <div className="d-inline-flex align-items-center">
                                                <span className="text-primary me-1">₹</span>
                                                <input
                                                  type="number"
                                                  step="0.01"
                                                  className="form-control form-control-sm p-1"
                                                  style={{ width: '70px', height: '28px' }}
                                                  name="our_marketing_price"
                                                  value={editForm.our_marketing_price}
                                                  onChange={handleInputChange}
                                                />
                                              </div>
                                            ) : (
                                              <span className="fw-bold text-primary">₹{record.our_marketing_price}</span>
                                            )}
                                          </td>

                                          {/* UTILITY */}
                                          <td>
                                            <span className="text-muted">₹{record.meta_utility_price}</span>
                                            <span className="mx-1">|</span>
                                            {isEditing ? (
                                              <div className="d-inline-flex align-items-center">
                                                <span className="text-primary me-1">₹</span>
                                                <input
                                                  type="number"
                                                  step="0.01"
                                                  className="form-control form-control-sm p-1"
                                                  style={{ width: '70px', height: '28px' }}
                                                  name="our_utility_price"
                                                  value={editForm.our_utility_price}
                                                  onChange={handleInputChange}
                                                />
                                              </div>
                                            ) : (
                                              <span className="fw-bold text-primary">₹{record.our_utility_price}</span>
                                            )}
                                          </td>

                                          {/* AUTHENTICATION */}
                                          <td>
                                            <span className="text-muted">₹{record.meta_authentication_price}</span>
                                            <span className="mx-1">|</span>
                                            {isEditing ? (
                                              <div className="d-inline-flex align-items-center">
                                                <span className="text-primary me-1">₹</span>
                                                <input
                                                  type="number"
                                                  step="0.01"
                                                  className="form-control form-control-sm p-1"
                                                  style={{ width: '70px', height: '28px' }}
                                                  name="our_authentication_price"
                                                  value={editForm.our_authentication_price}
                                                  onChange={handleInputChange}
                                                />
                                              </div>
                                            ) : (
                                              <span className="fw-bold text-primary">₹{record.our_authentication_price}</span>
                                            )}
                                          </td>

                                          {/* SERVICE */}
                                          <td>
                                            <span className="text-muted">₹{record.meta_service_price}</span>
                                            <span className="mx-1">|</span>
                                            {isEditing ? (
                                              <div className="d-inline-flex align-items-center">
                                                <span className="text-primary me-1">₹</span>
                                                <input
                                                  type="number"
                                                  step="0.01"
                                                  className="form-control form-control-sm p-1"
                                                  style={{ width: '70px', height: '28px' }}
                                                  name="our_service_price"
                                                  value={editForm.our_service_price}
                                                  onChange={handleInputChange}
                                                />
                                              </div>
                                            ) : (
                                              <span className="fw-bold text-primary">₹{record.our_service_price}</span>
                                            )}
                                          </td>

                                          <td className="text-center">
                                            {record.last_synced ? moment(record.last_synced).format('DD MMM, HH:mm') : 'Never'}
                                          </td>

                                          <td className="text-center">
                                            {isEditing ? (
                                              <div className="d-flex align-items-center justify-content-center gap-1">
                                                <button
                                                  onClick={() => handleSaveEdit(record)}
                                                  className="btn btn-icon btn-sm btn-soft-success btn-rounded"
                                                  disabled={isSaving}
                                                  title="Save"
                                                >
                                                  <span className="icon">
                                                    <i className={isSaving ? "ri-loader-4-line fa-spin" : "ri-check-line"}></i>
                                                  </span>
                                                </button>
                                                <button
                                                  onClick={handleCancelEdit}
                                                  className="btn btn-icon btn-sm btn-soft-danger btn-rounded"
                                                  disabled={isSaving}
                                                  title="Cancel"
                                                >
                                                  <span className="icon"><i className="ri-close-line"></i></span>
                                                </button>
                                              </div>
                                            ) : (
                                              <div className="d-flex align-items-center justify-content-center gap-1">
                                                <button
                                                  onClick={() => handleEditClick(record)}
                                                  className="btn btn-icon btn-sm btn-flush-dark btn-border flush-soft-hover"
                                                  title="Edit Margins"
                                                >
                                                  <span className="icon">
                                                    <i className="ri-pencil-fill"></i>
                                                  </span>
                                                </button>
                                              </div>
                                            )}
                                          </td>
                                        </tr>
                                      );
                                    })
                                  ) : (
                                    !loading && (
                                      <tr>
                                        <td colSpan={7} className="text-center py-5">
                                          <div className="text-muted">
                                            <i className="ri-earth-line fs-1 mb-2 d-block"></i>
                                            No pricing data found.
                                          </div>
                                        </td>
                                      </tr>
                                    )
                                  )}
                                </tbody>
                              </table>
                            </div>

                            <div className="card-footer d-flex justify-content-between align-items-center">
                              <div className="col-sm">
                                <div className="text-muted">
                                  Showing <span className="fw-semibold">{pricingList?.length || 0}</span> of <span className="fw-semibold">{pagination.total || 0}</span> Countries
                                </div>
                              </div>
                              {pagination.totalPages > 0 && (
                                <PaginationComponent
                                  totalPages={pagination.totalPages}
                                  currentPage={page}
                                  setCurrentPage={setPage}
                                />
                              )}
                            </div>

                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default MetaPricingSync;